// src/hooks/useAccessLevel.ts
import { useUser } from "@clerk/clerk-react";
import { useBrandConfig } from "./use-brand-config";
import { useDevMember } from "@/dev/DevMemberContext";

export const useAccessLevel = () => {
  const { user, isLoaded } = useUser();
  const { brandConfig } = useBrandConfig();
  const { membershipOverride } = useDevMember();

  // DEV MODE: If there's a membership override from DevMemberContext, use it
  if (membershipOverride) {
    const { isCustomer, isCommunityMember, isAaron, memberSince } = membershipOverride;
    const hasUnlimitedAccess = isCustomer || isCommunityMember || isAaron;

    const permissions = [];
    if (isCustomer) permissions.push("customer");
    if (isCommunityMember) permissions.push("community");
    if (isAaron) permissions.push("aaron");

    return {
      isLoaded: true,
      isCustomer,
      isCommunityMember,
      isAaron,
      hasUnlimitedAccess,
      memberSince: memberSince ? new Date(memberSince) : null,
      permissions,
    };
  }

  // If brand config grants community access, override normal logic
  if (brandConfig.features?.grantCommunityAccess) {
    return {
      isLoaded: true,
      isCustomer: true, // Community members are also considered customers
      isCommunityMember: true,
      isAaron: false,
      hasUnlimitedAccess: true,
      memberSince: null,
      permissions: ["customer", "community"],
    };
  }

  if (!isLoaded) {
    return {
      isLoaded: false,
      isCustomer: false,
      isCommunityMember: false,
      isAaron: false,
      hasUnlimitedAccess: false,
      memberSince: null,
      permissions: [],
    };
  }

  // User is not signed in but Clerk has loaded - we know this state
  if (!user) {
    return {
      isLoaded: true,
      isCustomer: false,
      isCommunityMember: false,
      isAaron: false,
      hasUnlimitedAccess: false,
      memberSince: null,
      permissions: [],
    };
  }

  const membership =
    (user.unsafeMetadata?.membership as Record<string, unknown>) || {};

  const isCommunityMember = Boolean(membership.isCommunityMember);
  const isCustomer = Boolean(membership.isCustomer) || isCommunityMember;
  const isAaron = Boolean(membership.isAaron);

  const hasUnlimitedAccess = isCustomer || isAaron;

  const memberSince = membership.memberSince
    ? new Date(membership.memberSince as string)
    : null;

  const permissions = [];
  if (isCustomer) permissions.push("customer");
  if (isCommunityMember) permissions.push("community");
  if (isAaron) permissions.push("aaron");

  return {
    isLoaded: true,
    isCustomer,
    isCommunityMember,
    isAaron,
    hasUnlimitedAccess,
    memberSince,
    permissions,
  };
};
